import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Skull, Search, RefreshCw, Save, Mail, ShieldAlert, ShieldCheck, X, CheckCircle2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => {
  const t = localStorage.getItem('access_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const STATUS_META = {
  signed:            { label: 'SIGNED',            color: 'bg-emerald-600 text-white border-emerald-300' },
  pending_guardian:  { label: 'PENDING GUARDIAN',  color: 'bg-yellow-500 text-black border-yellow-200' },
  declined:          { label: 'DECLINED',          color: 'bg-red-600 text-white border-red-300' },
};

export default function HHWaiversTab() {
  const [tab, setTab] = useState('list');
  return (
    <div className="space-y-4">
      <div className="flex gap-2 border-b border-border">
        <button
          data-testid="hhw-tab-list"
          onClick={() => setTab('list')}
          className={`px-3 py-2 font-heading text-xs tracking-widest uppercase border-b-2 ${tab === 'list' ? 'border-red-500 text-red-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Waivers List
        </button>
        <button
          data-testid="hhw-tab-editor"
          onClick={() => setTab('editor')}
          className={`px-3 py-2 font-heading text-xs tracking-widest uppercase border-b-2 ${tab === 'editor' ? 'border-red-500 text-red-400' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
        >
          Legal Text Editor
        </button>
      </div>
      {tab === 'list' && <WaiversList />}
      {tab === 'editor' && <LegalEditor />}
    </div>
  );
}

function WaiversList() {
  const [waivers, setWaivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (status) params.status = status;
      const r = await axios.get(`${API}/api/admin/hh/waivers`, { headers: authHeaders(), params });
      setWaivers(r.data.waivers || []);
    } catch {
      toast.error('Failed to load waivers');
    } finally { setLoading(false); }
  }, [q, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3">
      <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
        <div className="flex-1 flex items-center gap-2 bg-background border border-border rounded-lg px-3 py-2 focus-within:border-red-500">
          <Search size={14} className="text-muted-foreground" />
          <input
            type="text" value={q} onChange={e => setQ(e.target.value)}
            data-testid="hhw-search"
            onKeyDown={e => e.key === 'Enter' && load()}
            placeholder="Search by name, email, order # or waiver ID"
            className="w-full bg-transparent outline-none text-sm"
          />
        </div>
        <select
          data-testid="hhw-status-filter"
          value={status}
          onChange={e => setStatus(e.target.value)}
          className="bg-background border border-border rounded-lg px-3 py-2 text-sm outline-none focus:border-red-500"
        >
          <option value="">All statuses</option>
          <option value="signed">Signed</option>
          <option value="pending_guardian">Pending Guardian</option>
          <option value="declined">Declined</option>
        </select>
        <button
          onClick={load} data-testid="hhw-refresh"
          className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-heading text-xs tracking-widest uppercase px-4 py-2 rounded-full"
        >
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading waivers…</p>}

      {!loading && waivers.length === 0 && (
        <div className="text-center py-10 text-muted-foreground">
          <Skull className="mx-auto mb-2 opacity-40" size={40} />
          <p className="text-sm">No haunted house waivers yet.</p>
        </div>
      )}

      <div className="space-y-2">
        {waivers.map(w => {
          const meta = STATUS_META[w.status] || { label: (w.status || '').toUpperCase(), color: 'bg-neutral-700 text-white border-neutral-500' };
          return (
            <div
              key={w.waiver_id}
              data-testid={`hhw-row-${w.waiver_id}`}
              className="rounded-xl border border-border bg-card p-3 hover:border-red-500/40 transition-colors cursor-pointer"
              onClick={() => setDetail(w.waiver_id)}
            >
              <div className="flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-heading tracking-wide">{w.leader_name || w.signer_name || '(no name)'}</span>
                    <span className={`font-heading text-[9px] tracking-widest uppercase px-2 py-0.5 rounded border ${meta.color}`}>{meta.label}</span>
                    {w.order_num && <span className="font-mono text-[10px] text-orange-300 bg-orange-950/40 border border-orange-500/40 px-1.5 rounded">{w.order_num}</span>}
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground truncate">
                    {w.email} · {w.guest_count} guest{w.guest_count === 1 ? '' : 's'}
                    {w.minor_count > 0 ? ` · ${w.minor_count} minor${w.minor_count === 1 ? '' : 's'} (${w.minors_pending}p/${w.minors_approved}a)` : ''}
                    {w.event_date ? ` · ${w.event_date}` : ''}
                  </div>
                  <div className="mt-1 text-[10px] text-muted-foreground">
                    {(w.guests || []).map(g => g.name).join(', ')}
                  </div>
                </div>
                <div className="shrink-0 text-[10px] text-muted-foreground font-mono">{w.waiver_id}</div>
              </div>
            </div>
          );
        })}
      </div>

      {detail && <WaiverDetailModal waiverId={detail} onClose={() => setDetail(null)} onChanged={load} />}
    </div>
  );
}

function WaiverDetailModal({ waiverId, onClose, onChanged }) {
  const [w, setW] = useState(null);
  const [err, setErr] = useState('');
  const [resending, setResending] = useState(-1);

  useEffect(() => {
    axios.get(`${API}/api/admin/hh/waivers/${waiverId}`, { headers: authHeaders() })
      .then(r => setW(r.data))
      .catch(() => setErr('Failed to load'));
  }, [waiverId]);

  const resendGuardian = async (idx) => {
    setResending(idx);
    try {
      const r = await axios.post(`${API}/api/admin/hh/waivers/${waiverId}/resend-guardian/${idx}`,
        {}, { headers: authHeaders() });
      toast.success(`Guardian email re-sent to ${r.data?.resent_to || 'guardian'}`);
      onChanged && onChanged();
      // Refresh
      const rr = await axios.get(`${API}/api/admin/hh/waivers/${waiverId}`, { headers: authHeaders() });
      setW(rr.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Resend failed');
    } finally { setResending(-1); }
  };

  return (
    <div className="fixed inset-0 z-[9998] bg-black/85 flex items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        className="bg-neutral-950 border-2 border-red-600/60 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        style={{ boxShadow: '0 0 40px rgba(220,38,38,0.4)' }}
      >
        <div className="bg-gradient-to-r from-red-800 to-red-900 p-4 flex items-center justify-between">
          <div className="flex items-center gap-2 text-white">
            <Skull size={18} />
            <h3 className="font-heading tracking-widest uppercase">Waiver Detail</h3>
          </div>
          <button onClick={onClose} data-testid="hhw-detail-close" className="w-8 h-8 rounded-full bg-black/40 flex items-center justify-center text-white"><X size={16} /></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-4">
          {err && <p className="text-red-400">{err}</p>}
          {!w && !err && <p className="text-muted-foreground">Loading…</p>}
          {w && (
            <>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Info label="Waiver ID" value={w.waiver_id} />
                <Info label="Status" value={(w.status || '').toUpperCase()} />
                <Info label="Leader" value={w.leader_name || w.signer_name} />
                <Info label="Signature" value={w.leader_signature} />
                <Info label="DOB" value={w.leader_date_of_birth} />
                <Info label="Age" value={w.leader_age} />
                <Info label="Email" value={w.email} />
                <Info label="Phone" value={w.phone} />
                <Info label="Emergency Contact" value={w.emergency_contact_name} />
                <Info label="Emergency Phone" value={w.emergency_contact_phone} />
                <Info label="Photo Consent" value={w.photo_consent === true ? 'YES' : w.photo_ask ? 'ASK HOW PHOTOS ARE USED' : w.photo_consent === false ? 'NO' : '—'} />
                <Info label="Rules Acknowledged" value={w.rules_acknowledged ? 'YES' : '—'} />
                <Info label="Order #" value={w.order_num} />
                <Info label="Event Date" value={w.event_date} />
                <Info label="Guardian" value={w.guardian_full_name || '—'} />
                <Info label="Guardian Rel." value={w.guardian_relationship || '—'} />
                <Info label="Guardian Phone" value={w.guardian_phone || '—'} />
                <Info label="Guardian Sig" value={w.guardian_signature_leader || '—'} />
                <Info label="Submitted" value={w.submitted_at ? new Date(w.submitted_at).toLocaleString() : ''} />
                <Info label="IP" value={w.ip} />
              </div>

              <div>
                <p className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1">Guests</p>
                <div className="space-y-2">
                  {(w.guests || []).map((g, i) => (
                    <div key={i} className="rounded-lg border border-border bg-black/40 p-3 text-xs">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-heading tracking-wide text-sm">{g.name}</span>
                        {g.is_leader && <span className="bg-red-800 text-white text-[9px] font-heading tracking-widest uppercase px-1.5 py-0.5 rounded">LEADER</span>}
                        {g.is_minor && <span className="bg-yellow-600 text-black text-[9px] font-heading tracking-widest uppercase px-1.5 py-0.5 rounded">MINOR</span>}
                        {g.is_minor && (
                          <span className={`text-[9px] font-heading tracking-widest uppercase px-1.5 py-0.5 rounded ${
                            g.guardian_status === 'approved' ? 'bg-emerald-700 text-white' :
                            g.guardian_status === 'declined' ? 'bg-red-700 text-white' :
                            'bg-neutral-700 text-white'
                          }`}>
                            GUARDIAN {(g.guardian_status || 'pending').toUpperCase()}
                          </span>
                        )}
                      </div>
                      {g.is_minor && (
                        <div className="mt-1 text-[11px] text-muted-foreground">
                          {g.guardian_email && <div>Guardian email: {g.guardian_email}</div>}
                          {g.guardian_name && <div>Guardian signature: {g.guardian_name} — {g.guardian_signature}</div>}
                          {g.guardian_responded_at && <div>Responded: {new Date(g.guardian_responded_at).toLocaleString()}</div>}
                          {g.guardian_status === 'pending' && (
                            <button
                              onClick={() => resendGuardian(i)}
                              disabled={resending === i}
                              data-testid={`hhw-resend-guardian-${i}`}
                              className="mt-2 inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white font-heading text-[10px] tracking-widest uppercase px-3 py-1 rounded-full disabled:opacity-50"
                            >
                              <Mail size={11} /> {resending === i ? 'Sending…' : 'Resend Guardian Email'}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div className="bg-black/30 border border-border rounded p-2">
      <div className="text-[9px] text-muted-foreground font-heading tracking-widest uppercase">{label}</div>
      <div className="text-xs font-mono break-all">{value || '—'}</div>
    </div>
  );
}

function LegalEditor() {
  const [en, setEn] = useState('');
  const [ja, setJa] = useState('');
  const [enabled, setEnabled] = useState(true);
  const [version, setVersion] = useState('1');
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/admin/hh/waiver-settings`, { headers: authHeaders() })
      .then(r => {
        setEn(r.data.legal_text_en || '');
        setJa(r.data.legal_text_ja || '');
        setEnabled(!!r.data.enabled);
        setVersion(r.data.version || '1');
      })
      .catch(() => toast.error('Failed to load'))
      .finally(() => setLoading(false));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/api/admin/hh/waiver-settings`,
        { legal_text_en: en, legal_text_ja: ja, enabled },
        { headers: authHeaders() });
      setVersion(r.data.version || '1');
      toast.success(`Saved (v${r.data.version})`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  if (loading) return <p className="text-sm text-muted-foreground">Loading legal text…</p>;

  return (
    <div className="space-y-4">
      <label className="inline-flex items-center gap-2 text-sm cursor-pointer select-none">
        <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)}
          data-testid="hhw-enabled-toggle"
          className="w-4 h-4 accent-red-500" />
        <span>Waiver enabled (guests can submit)</span>
        <span className="ml-2 text-[10px] text-muted-foreground font-mono">v{version}</span>
      </label>

      <div>
        <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Legal Text (EN)</label>
        <textarea value={en} onChange={e => setEn(e.target.value)}
          data-testid="hhw-legal-en"
          rows={12}
          className="w-full bg-background border border-border rounded-lg px-3 py-2 font-mono text-xs outline-none focus:border-red-500" />
      </div>
      <div>
        <label className="font-heading text-[10px] tracking-widest uppercase text-muted-foreground mb-1 block">Legal Text (JA)</label>
        <textarea value={ja} onChange={e => setJa(e.target.value)}
          data-testid="hhw-legal-ja"
          rows={12}
          className="w-full bg-background border border-border rounded-lg px-3 py-2 font-mono text-xs outline-none focus:border-red-500" />
      </div>

      <button onClick={save} disabled={saving}
        data-testid="hhw-save"
        className="inline-flex items-center gap-2 bg-red-600 hover:bg-red-500 text-white font-heading tracking-widest uppercase text-sm px-6 py-3 rounded-full disabled:opacity-60">
        {saving ? <RefreshCw className="animate-spin" size={14} /> : <Save size={14} />}
        {saving ? 'Saving…' : 'Save Waiver Text'}
      </button>
    </div>
  );
}
